import { computed, type Component, type Ref } from 'vue'
import { Upload, FolderPlus, Briefcase, Building2 } from 'lucide-vue-next'
import type { DocumentSource } from '~/composables/documents/sources'

export interface ExplorerCreateAction { key: string; label: string; icon: Component; run: () => void }

export interface ExplorerCreateActionsDeps {
  t: (key: string) => string
  isFolderMode: Ref<boolean>
  activeSources: Ref<Set<DocumentSource>>
  startCreateFolder: () => void
  pickFiles: () => void
  go: (path: string) => void
}

/**
 * One mode-aware create-action list feeding BOTH the "+ New" dropdown and the empty-state buttons
 * of the documents sidebar (B0.4 — replaces two hard-coded, mode-blind folder-only copies; split out
 * of `FileExplorer.vue` in MAR-948).
 *
 * In folder mode it is New Folder + Upload. Otherwise it is one entry per SELECTED source that has
 * an entity to create, in canonical order — the enum version could only ever offer one
 * (AlexAmin/marcus#158) — plus Upload. Hojas and Conversaciones contribute none: a sheet is made from
 * a selection of documents and a thread by asking a question, so neither has a "new empty one" that
 * belongs in the document browser's create menu.
 */
export function useExplorerCreateActions(deps: ExplorerCreateActionsDeps) {
  const { t, isFolderMode, activeSources, startCreateFolder, pickFiles, go } = deps
  return computed<ExplorerCreateAction[]>(() => {
    const upload: ExplorerCreateAction = { key: 'upload', label: t('common.uploadFiles'), icon: Upload, run: pickFiles }
    if (isFolderMode.value) {
      return [{ key: 'new-folder', label: t('common.newFolder'), icon: FolderPlus, run: startCreateFolder }, upload]
    }
    const out: ExplorerCreateAction[] = []
    if (activeSources.value.has('project')) {
      out.push({ key: 'new-client', label: t('clients.newClient'), icon: Building2, run: () => go('/app/clients/new') })
    }
    if (activeSources.value.has('case')) {
      out.push({ key: 'new-case', label: t('clients.newCase'), icon: Briefcase, run: () => go('/app/cases/new') })
    }
    out.push(upload)
    return out
  })
}
